/**
 * Shared column-ordering helper for `useColumnPreferences` consumers.
 *
 * Both the media-units catalog (`useMediaUnitsPage`) and the offer units tab
 * (`UnitsTabPage`) need to turn their column-def array + user-defined
 * `columnOrder` + `visibleColumnIds` (all from `useColumnPreferences`) into an
 * ordered list for the "Kolumny" picker and a filtered, ordered list for the
 * actual table render. This was previously duplicated verbatim in both files.
 */

/** Minimal shape required of a column definition to be ordered/filtered. */
interface ColumnLike {
  id: string;
  width?: number;
  fixed?: boolean;
  resizable?: boolean;
}

/**
 * Overrides a column's static `width` from a persisted `columnWidths` map, unless the
 * column is structural (`fixed` or `resizable: false` — DataTable never renders a resize
 * handle for these, so a stored width for one would only ever come from a stale/malformed
 * localStorage entry). Returns the column unchanged when no override applies.
 */
export function applyStoredWidth<T extends ColumnLike>(
  col: T,
  columnWidths: Readonly<Record<string, number>>,
): T {
  if (col.fixed || col.resizable === false) return col;
  const stored = columnWidths[col.id];
  if (stored === undefined) return col;
  return { ...col, width: stored };
}

/**
 * Orders `columns` by the user-defined `columnOrder`, then filters that ordered
 * list down to only the currently visible column ids.
 *
 * @param columnWidths Persisted per-column pixel widths from `useColumnPreferences`.
 *   Overrides each non-structural visible column's static `width`.
 * @returns `orderedColumns` — all `columns` sorted by user-defined order
 *   (used by the "Kolumny" picker so its list order matches the table).
 *   Role-based filtering, if any, is the caller's responsibility upstream
 *   of this function — it has no knowledge of roles.
 * @returns `visibleColumns` — `orderedColumns` filtered by user-driven
 *   visibility, in user-defined order, with stored widths applied (used for
 *   the actual table render).
 */
export function buildOrderedVisibleColumns<T extends ColumnLike>(
  columns: T[],
  columnOrder: string[],
  visibleColumnIds: ReadonlySet<string>,
  columnWidths: Readonly<Record<string, number>>,
): { orderedColumns: T[]; visibleColumns: T[] } {
  const colById = new Map(columns.map((c) => [c.id, c]));
  const orderedColumns = columnOrder
    .map((id) => colById.get(id))
    .filter((c): c is T => c !== undefined);
  const visibleColumns = orderedColumns
    .filter((c) => visibleColumnIds.has(c.id))
    .map((c) => applyStoredWidth(c, columnWidths));

  return { orderedColumns, visibleColumns };
}
